import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Click_DashRenameLink } from "@mirage/analytics/events/types/click_dash_rename_link";
import { PAP_Delete_DashStack } from "@mirage/analytics/events/types/delete_dash_stack";
import { PAP_Leave_DashStack } from "@mirage/analytics/events/types/leave_dash_stack";
import { PAP_Pin_DashStack } from "@mirage/analytics/events/types/pin_dash_stack";
import { PAP_Unpin_DashStack } from "@mirage/analytics/events/types/unpin_dash_stack";
import { PAP_Update_StackIconColor } from "@mirage/analytics/events/types/update_stack_icon_color";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import { tagged } from "@mirage/service-logging";
import {
  logPageLoadMilestone,
  logPageLoadMilestoneOnce,
} from "@mirage/service-operational-metrics/page-load";
import { useLastViewedStackInfo } from "@mirage/service-recent-content/hooks/useLastViewedStackInfo";
import {
  deleteStack,
  leaveStack,
  listAllStackItemsFromCache,
  listAllStackItemsOneByOne,
  listStacksIncrementally,
  previewPublicStack,
  previewStack,
  subscribeToStackItemsUpdates,
  subscribeToStackMutation,
  subscribeToStacksUpdates,
  updateStack,
  updateStackItem,
  upsertStack,
} from "@mirage/service-stacks";
import {
  ALWAYS_TRUE,
  asShortcut,
  getPredicateFromFilterPreference,
  isStackItemCreator,
  isStackItemShortcut,
  sortStackItemsBySortKey,
  sortStacksByPinStatusAndHlc,
  sortStacksBySortOption,
  stackDerivePAPProps,
  stackForDiffing,
  stackGetShareId,
  stackItemComputeNameFromFields,
  stacksForDiffing,
} from "@mirage/service-stacks/service/utils";
import { metadataForUrls } from "@mirage/service-url-metadata";
import { useMetadataForUrls } from "@mirage/service-url-metadata/hooks";
import Sentry from "@mirage/shared/sentry";
import { showSnackbar } from "@mirage/shared/snackbar";
import { ONE_MINUTE_IN_MILLIS } from "@mirage/shared/util/constants";
import { isErrorWithMessage } from "@mirage/shared/util/error";
import { nonNil, sleepMs } from "@mirage/shared/util/tiny-utils";
import { useIsCompanyStacksEnabled } from "@mirage/stacks/hooks/useIsCompanyStacksEnabled";
import { StackTheme } from "@mirage/stacks/themes";
import {
  isValidHexColor,
  mergeAndAggregateUniqueStacksByNamespaceId,
} from "@mirage/stacks/utils";
import i18n from "@mirage/translations";
import { useDeepCompareEffect, useDeepCompareMemo } from "@react-hookz/web";
import { atom, useAtom, useAtomValue } from "jotai";
import { uniqBy } from "lodash";
import isEqual from "lodash/isEqual";
import partition from "lodash/partition";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { dedupedAtom } from "../../shared/util/jotai";
import {
  isStackItemWithUrl,
  isStackOwner,
  stackIsArchived,
} from "../Helpers/Utils";

import type {
  DropboxResponseError,
  stacks,
  users,
} from "@dropbox/api-v2-client/types";
import type {
  PAPEvent,
  PAPEventDefaultProperties,
} from "@mirage/analytics/events/base/event";
import { PAP_Archive_DashStacksArchive } from "@mirage/analytics/events/types/archive_dash_stacks_archive";
import { PAP_Rename_DashStack } from "@mirage/analytics/events/types/rename_dash_stack";
import { PAP_Undo_DashStacksArchive } from "@mirage/analytics/events/types/undo_dash_stacks_archive";
import { PAP_Update_StackEmoji } from "@mirage/analytics/events/types/update_stack_emoji";
import { PAP_Update_StacksDescription } from "@mirage/analytics/events/types/update_stacks_description";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import type { LastViewedStackInfo } from "@mirage/service-recent-content/types";
import type {
  StackFilterOption,
  StackSortPreference,
} from "@mirage/service-settings/service/types";
import type { StackMutationEvent } from "@mirage/service-stacks/service";
import {
  isSummarySupported,
  type StackItem,
  type StackItemShortcutWithMetadata,
} from "@mirage/service-stacks/service/types";
import type { UrlMetadata } from "@mirage/service-url-metadata/types";
import {
  MAX_STACK_DESCRIPTION_LENGTH,
  MAX_STACK_NAME_LENGTH,
} from "@mirage/shared/util/constants";
import type { Subscription } from "rxjs";
import type { TabSuggestion } from "../AddStackItemMenuContent/types";
import { convertStackItemToChatSource } from "../FullScreenStack/utils";
import {
  asStackUpsertId,
  newStackPapEvent,
  sanitizedStackName,
} from "../StackDetailPage/utils";

const logger = tagged("stacks-hooks");

export type UpdateStackDescriptionCallback = (
  newDescription: string,
) => Promise<false | stacks.Stack | undefined>;

export type UpdateStackEmojiCallback = (
  newEmoji: string,
) => Promise<false | stacks.Stack | undefined>;

export type UpdateStackNameCallback = (
  newName: string,
) => Promise<false | stacks.Stack | undefined>;

export const useUpdateStackDescription = (
  stack: stacks.Stack | null,
  canEdit: boolean,
  mutationRequestId?: string,
  sessionId?: string,
): UpdateStackDescriptionCallback => {
  const updateStackProperty = useUpdateStackProperty(
    stack,
    mutationRequestId,
    sessionId,
  );
  return async (newDescription: string) => {
    if (!canEdit) {
      logger.warn("Trying to edit a stack without permission");
      showSnackbar({ title: i18n.t("no_permission_to_edit_stack") });
      return false;
    }
    const finalDescription = newDescription.trim();
    if (finalDescription.length > MAX_STACK_DESCRIPTION_LENGTH) {
      showSnackbar({
        title: i18n.t("input_too_long"),
      });
      return false;
    }
    const updates: stacks.StackDataUpdate[] = [
      {
        field: {
          description_update: finalDescription,
          ".tag": "description_update",
        },
      },
    ];
    try {
      const stack = await updateStackProperty(updates, [
        PAP_Update_StacksDescription,
      ]);
      return stack;
    } catch (e) {
      showSnackbar({ title: i18n.t("failed_to_update_stack") });
      logger.error("Error updating stack description", e);
      Sentry.captureException(e);
      return false;
    }
  };
};

export const useUpdateStackEmoji = (
  stack: stacks.Stack | null,
  mutationRequestId?: string,
  sessionId?: string,
): UpdateStackEmojiCallback => {
  const updateStackProperty = useUpdateStackProperty(
    stack,
    mutationRequestId,
    sessionId,
  );

  return useCallback(
    async (newEmoji: string) => {
      try {
        const stack = await updateStackProperty(
          [
            {
              field: { emoji_update: newEmoji, ".tag": "emoji_update" },
            },
          ],
          [PAP_Update_StackEmoji],
        );
        return stack;
      } catch (e) {
        showSnackbar({ title: i18n.t("failed_to_update_emoji") });
        logger.error("Error updating stack emoji", e);
        Sentry.captureException(e);
      }
    },
    [updateStackProperty],
  );
};

export const useUpdateStackName = (
  stack: stacks.Stack | null,
  canEdit: boolean,
  mutationRequestId?: string,
  sessionId?: string,
): UpdateStackNameCallback => {
  const updateStackProperty = useUpdateStackProperty(
    stack,
    mutationRequestId,
    sessionId,
  );

  return async (newName: string) => {
    if (!canEdit) {
      logger.warn("Trying to edit a stack without permission");
      showSnackbar({ title: i18n.t("no_permission_to_edit_stack") });
      return false;
    }

    const trimmedStackName = newName.trim();
    const sanitizedName = sanitizedStackName(trimmedStackName);
    if (sanitizedName !== trimmedStackName) {
      showSnackbar({
        title: i18n.t("illegal_characters_warning"),
      });
      return false;
    }
    const finalStackName =
      sanitizedName.length === 0
        ? i18n.t("untitled_stack")
        : sanitizedName.replace(/\n+/gi, "\n");
    if (finalStackName.length > MAX_STACK_NAME_LENGTH) {
      showSnackbar({
        title: i18n.t("input_too_long"),
      });
      return false;
    }
    const updates: stacks.StackDataUpdate[] = [
      {
        field: { name_update: finalStackName, ".tag": "name_update" },
      },
    ];
    try {
      const stack = await updateStackProperty(updates, [PAP_Rename_DashStack]);
      return stack;
    } catch (e) {
      showSnackbar({ title: i18n.t("failed_to_update_stack") });
      logger.error("Error updating stack name", e);
      Sentry.captureException(e);
      return false;
    }
  };
};

const useUpdateStackProperty = (
  stack: stacks.Stack | null,
  mutationRequestId?: string,
  sessionId?: string,
) => {
  const { reportPapEvent } = useMirageAnalyticsContext();

  return useCallback(
    async (
      updates: stacks.StackDataUpdate[],
      eventCreators: Array<(props: PAPEventDefaultProperties) => PAPEvent>,
    ) => {
      const { stack: createdStack } = await upsertStack(
        asStackUpsertId(stack?.namespace_id, mutationRequestId ?? null),
        updates,
      );

      if (stack) {
        const papProps = stackDerivePAPProps(stack);

        for (const eventCreator of eventCreators) {
          reportPapEvent(eventCreator({ ...papProps, featureLine: "stacks" }));
        }
      } else {
        reportPapEvent(newStackPapEvent(createdStack?.namespace_id, sessionId));
      }

      return createdStack;
    },
    [stack, reportPapEvent, mutationRequestId, sessionId],
  );
};

// https://dropbox.sourcegraphcloud.com/github.com/dropbox-internal/server/-/blob/atlas/stacks/stacks.py?L387
// Keeping max length consistent with the server side
export const MAX_STACK_ITEM_TITLE_LENGTH = 250;

// exported for testing only
export const unsortedStacksAtom = dedupedAtom<stacks.Stack[] | undefined>(
  undefined,
);

// exported for testing only
export const publishedContentsAtom = atom<stacks.PublishedContent[]>([]);

const allStackItemsAtom = dedupedAtom<
  { [nsid: string]: stacks.StackItem[] } | undefined
>(undefined);

const isDeleteStackModalOpenAtom = atom(false);

const stackToDeleteAtom = atom<stacks.Stack | undefined>(undefined);

const stackItemToEditAtom = atom<
  { stack: stacks.Stack; item: stacks.StackItemShortcut } | undefined
>(undefined);

export const useDeleteStackModal = (): {
  showDeleteConfirmationModal: boolean;
  setShowDeleteConfirmationModal: (x: boolean) => void;
  onDelete: () => void;
  setStackToDelete: (stack: stacks.Stack | undefined) => void;
  stackToDelete: stacks.Stack | undefined;
} => {
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useAtom(
    isDeleteStackModalOpenAtom,
  );
  const [stackToDelete, setStackToDelete] = useAtom(stackToDeleteAtom);
  const { reportPapEvent } = useMirageAnalyticsContext();

  const navigate = useNavigate();
  const location = useLocation();

  const onDelete = () => {
    if (!stackToDelete || !stackToDelete.namespace_id) return;
    reportPapEvent(
      PAP_Delete_DashStack({
        ...stackDerivePAPProps(stackToDelete),
        featureLine: "stacks",
        actionSurfaceComponent: "stacks_dropdown",
      }),
    );
    // Don't navigate if we delete from the homepage or stacks page
    if (
      location.pathname !== "/" &&
      location.pathname !== "/home" &&
      location.pathname !== "/stacks"
    ) {
      navigate("/stacks");
    }
    deleteStack(stackToDelete.namespace_id);
  };

  return {
    showDeleteConfirmationModal,
    setShowDeleteConfirmationModal,
    onDelete,
    setStackToDelete,
    stackToDelete,
  };
};

export const useEditStackItemModal = (): {
  stackItemAndStackToEdit:
    | { stack: stacks.Stack; item: stacks.StackItemShortcut }
    | undefined;
  setStackItemAndStackToEdit: (
    input: { stack: stacks.Stack; item: stacks.StackItemShortcut } | undefined,
  ) => void;
  onEditItemAndStack: (
    item: stacks.StackItemShortcut,
    stack: stacks.Stack,
  ) => void;
} => {
  const { reportPapEvent } = useMirageAnalyticsContext();
  const [stackItemAndStackToEdit, setStackItemAndStackToEdit] =
    useAtom(stackItemToEditAtom);

  const onEditItemAndStack = useCallback(
    (item: stacks.StackItemShortcut, stack: stacks.Stack) => {
      reportPapEvent(
        PAP_Click_DashRenameLink({
          ...stackDerivePAPProps(stack),
          featureLine: "stacks",
        }),
      );
      setStackItemAndStackToEdit({ item, stack });
    },
    [reportPapEvent, setStackItemAndStackToEdit],
  );

  return {
    stackItemAndStackToEdit,
    setStackItemAndStackToEdit,
    onEditItemAndStack,
  };
};

export function useInitStacksData() {
  logPageLoadMilestoneOnce("useInitStacksData start");
  useInitUnsortedStacks();
  useInitStackItems();
}

function useInitUnsortedStacks() {
  const [stacks, setStacks] = useAtom(unsortedStacksAtom);

  // A local copy of the stacks data allow us to decouple the subscription
  // from the data update if changed.
  const [localStacks, setLocalStacks] = useState<stacks.Stack[]>();

  // Subscribe to updates.
  useEffect(() => {
    let canceled = false;

    logPageLoadMilestone(`useInitUnsortedStacks subscribeToStacksUpdates`);

    const stacksSubscription = subscribeToStacksUpdates((rawStacks) => {
      if (canceled || !rawStacks) return;

      // Set local stacks to be copied to the atom below.
      setLocalStacks(rawStacks);
    });

    return () => {
      canceled = true;
      stacksSubscription.unsubscribe();
    };
  }, []);

  // Fetch initial data.
  useEffect(() => {
    let canceled = false;

    async function init() {
      logPageLoadMilestone(
        `useInitUnsortedStacks listStacksIncrementally start`,
      );
      const rawStacks = await listStacksIncrementally();
      if (canceled) return;

      logPageLoadMilestone(
        `useInitUnsortedStacks setLocalStacks: numStacks=${rawStacks.length}`,
      );
      setLocalStacks(rawStacks);
    }

    void init();

    return () => {
      canceled = true;
    };
  }, []);

  // Copy local stacks to stacks atom. Do this in a separate useEffect from
  // above to avoid unnecessary sub/unsub. Note that we only copy from
  // localStacks to unsortedStacksAtom, but never the other way round.
  useEffect(() => {
    const oldStacks = stacksForDiffing(stacks);
    const newStacks = stacksForDiffing(localStacks);

    if (!isEqual(newStacks, oldStacks)) {
      logPageLoadMilestone(`useInitUnsortedStacks setStacks from localStacks`);
      setStacks(localStacks);
    }
  }, [stacks, localStacks, setStacks]);

  return stacks;
}

export function useSortedStacks() {
  const stacks = useUnsortedStacksWithCompanyStacks();
  return useMemo(() => stacks && sortStacksByPinStatusAndHlc(stacks), [stacks]);
}

export const STACK_IS_PINNED = (stack: stacks.Stack) =>
  stack.user_data?.is_pinned ?? false;

export const STACK_NOT_PINNED = (stack: stacks.Stack) =>
  !stack.user_data?.is_pinned;

export function usePinnedStacksWithItems() {
  return useStacksByPredicate(STACK_IS_PINNED);
}

export function useSortedStacksWithItems(
  predicate: (stack: stacks.Stack) => boolean = ALWAYS_TRUE,
) {
  const { stacks, stackItemsMap } = useStacksByPredicate(predicate);
  const sortedStacks = useMemo(
    () => stacks && sortStacksByPinStatusAndHlc(stacks),
    [stacks],
  );
  return { stacks: sortedStacks, stackItemsMap };
}

export function useLastViewedStacksWithItems(
  lastViewedInfo: LastViewedStackInfo,
  predicate: (stack: stacks.Stack) => boolean = ALWAYS_TRUE,
) {
  const { stacks, stackItemsMap } = useStacksByPredicate(predicate);
  const sortedStacks = useMemo(
    () =>
      stacks && sortStacksByPinStatusAndHlc(stacks, undefined, lastViewedInfo),
    [stacks, lastViewedInfo],
  );
  return { stacks: sortedStacks, stackItemsMap };
}

export function useSortedStacksWithItemsBySortOptionWithLastViewedStackInfo(
  sortPreference?: StackSortPreference,
  filterPreference?: StackFilterOption,
  lastViewedStackInfo?: LastViewedStackInfo,
) {
  const predicate = useMemo(
    () => getPredicateFromFilterPreference(filterPreference),
    [filterPreference],
  );

  // Use existing cached sorted stacks and items
  const { stacks, stackItemsMap } = useSortedStacksWithItems(predicate);

  // Pre-cache the stacks by the available sort options
  const sortedStacks = useMemo(
    () =>
      stacks &&
      sortStacksBySortOption(stacks, sortPreference, lastViewedStackInfo),
    [stacks, sortPreference, lastViewedStackInfo],
  );

  return {
    stacks: sortedStacks,
    stackItemsMap,
  };
}

export function useSortedStacksWithItemsBySortOption(
  sortPreference?: StackSortPreference,
  filterPreference?: StackFilterOption,
) {
  const lastViewedStackInfo = useLastViewedStackInfo();
  return useSortedStacksWithItemsBySortOptionWithLastViewedStackInfo(
    sortPreference,
    filterPreference,
    lastViewedStackInfo,
  );
}

export function useStackByNsId(namespaceId: string | undefined) {
  const nsMatches = useCallback(
    (stack: stacks.Stack) => stack.namespace_id === namespaceId,
    [namespaceId],
  );
  return useStackByPredicate(namespaceId, nsMatches);
}

type StackData = {
  stack: stacks.Stack | null;
  items: stacks.StackItem[];
  error?: Error | DropboxResponseError<stacks.PreviewPublicStackError>;
};

export function usePreviewPublicStack(shareId: string | undefined) {
  const [stackData, setStackData] = useState<StackData | null>(null);

  useEffect(() => {
    if (shareId === undefined) {
      setStackData({ stack: null, items: [] });
      return;
    }

    let canceled = false;

    async function getPublicStack() {
      let stackData: StackData = { stack: null, items: [] };
      try {
        stackData = await previewPublicStack(shareId as string);
      } catch (e) {
        logger.error("Failed to retrieve public stack preview data", e);
        stackData.error = e as
          | Error
          | DropboxResponseError<stacks.PreviewPublicStackError>;
      }

      if (!canceled) {
        setStackData(stackData);
      }
    }

    void getPublicStack();

    return () => {
      canceled = true;
    };
  }, [shareId]);

  return stackData;
}

export function useStackByShareId(shareId: string | undefined) {
  const shareIdMatches = useCallback(
    (stack: stacks.Stack) => stackGetShareId(stack) === shareId,
    [shareId],
  );
  return useStackByPredicate(shareId, shareIdMatches);
}

export function useStacksByNsIds(namespaceIds: string[]) {
  const predicate = useCallback(
    (stack: stacks.Stack) => namespaceIds.includes(stack.namespace_id ?? ""),
    [namespaceIds],
  );
  return useStacksByPredicate(predicate);
}

function useInitStackItems() {
  // const stacks = useUnsortedStacks(); // Note: don't use merged content yet, since we don't fetch company pinned stack, items
  const stacks = useUnsortedStacksWithCompanyStacks();
  const [stackItemsMap, setStackItemsMap] = useAtom(allStackItemsAtom);

  // A local copy of the stacks data allow us to decouple the subscription
  // from the data update if changed.
  const [localStackItemsMap, setLocalStackItemsMap] = useState<{
    [nsid: string]: stacks.StackItem[];
  }>();

  // Subscribe to new data.
  useEffect(() => {
    let canceled = false;

    logPageLoadMilestone(`useInitStackItems subscribeToStackItemsUpdates`);

    const stackItemsSubscription = subscribeToStackItemsUpdates(
      ({ namespaceId, items }) => {
        if (!canceled) {
          sortStackItemsBySortKey(items);

          setLocalStackItemsMap((oldMap) => {
            return { ...oldMap, [namespaceId]: items };
          });
        }
      },
    );

    return () => {
      canceled = true;
      stackItemsSubscription.unsubscribe();
    };
  }, []);

  // Copy local items to items atom. Do this in a separate useEffect from
  // above to avoid unnecessary sub/unsub.
  useEffect(() => {
    if (!isEqual(stackItemsMap, localStackItemsMap)) {
      logPageLoadMilestone(
        `useInitStackItems setStackItemsMap from localStackItemsMap`,
      );
      setStackItemsMap(localStackItemsMap);
    }
  }, [localStackItemsMap, setStackItemsMap, stackItemsMap]);

  // Fetch initial data.
  useEffect(() => {
    async function initStackItems() {
      const itemsByNsId = await listAllStackItemsFromCache();

      if (itemsByNsId !== undefined) {
        logPageLoadMilestone(
          `useInitStackItems setLocalStackItemsMap: numStacks=${
            Object.keys(itemsByNsId).length
          }`,
        );
        setLocalStackItemsMap(itemsByNsId);
      }
    }

    // Don't await, let it run in the background.
    void initStackItems();
  }, []);

  // Fetch new data on stacks changes.
  useEffect(() => {
    // We'll need to refetch data whenever the stacks change.
    if (stacks === undefined) return;

    // Don't await, let it run in the background.
    logPageLoadMilestone(`useInitStackItems listAllStackItemsOneByOne`);
    void listAllStackItemsOneByOne();
  }, [stacks]);

  return { stacks, stackItemsMap };
}

export function useStacksByPredicate(
  predicate: (stack: stacks.Stack) => boolean,
) {
  const sortedStacks = useSortedStacks();
  const [stacks, setStacks] = useState<stacks.Stack[] | undefined>(undefined);
  const stackItemsMap = useAtomValue(allStackItemsAtom);

  useEffect(() => {
    // Filtered stacks can be undefined
    const filteredStacks = sortedStacks?.filter(predicate);

    const oldStacks = stacksForDiffing(stacks);
    const newStacks = stacksForDiffing(filteredStacks);

    if (!isEqual(oldStacks, newStacks)) {
      setStacks(filteredStacks);
    }
  }, [predicate, sortedStacks, stacks]);

  return { stacks, stackItemsMap };
}

function useStackByPredicate(
  shareId: string | undefined,
  predicate: (stack: stacks.Stack) => boolean,
) {
  const stacks = useUnsortedStacksWithCompanyStacks();
  const stackItemsMap = useAtomValue(allStackItemsAtom);

  const [stack, setStack] = useState<stacks.Stack>();
  const [items, setItems] = useState<stacks.StackItem[]>();

  useEffect(() => {
    if (!shareId) {
      setStack(undefined);
      setItems([]);
      return;
    }

    let canceled = false;

    const rawStack = stacks?.find(predicate);

    if (!canceled) {
      const oldStack = stack ? stackForDiffing(stack) : undefined;
      const newStack = rawStack ? stackForDiffing(rawStack) : undefined;

      if (!isEqual(newStack, oldStack)) {
        setStack(rawStack);
      }
    }

    return () => {
      canceled = true;
    };
  }, [stacks, shareId, predicate, stack]);

  useEffect(() => {
    const newItems =
      shareId && stack?.namespace_id
        ? stackItemsMap?.[stack?.namespace_id]
        : undefined;

    if (!isEqual(newItems, items)) {
      setItems(newItems);
    }
  }, [shareId, items, stack, stackItemsMap]);

  return { stack, items };
}

export function useReportToggleStackIsPinned() {
  const { reportPapEvent } = useMirageAnalyticsContext();

  const reportToggleStackIsPinned = useCallback(
    (stack: stacks.Stack) => {
      if (stack.user_data?.is_pinned) {
        reportPapEvent(
          PAP_Unpin_DashStack({
            ...stackDerivePAPProps(stack),
            actionSurfaceComponent: "stacks_dropdown",
            featureLine: "stacks",
          }),
        );
      } else {
        reportPapEvent(
          PAP_Pin_DashStack({
            ...stackDerivePAPProps(stack),
            actionSurfaceComponent: "stacks_dropdown",
            featureLine: "stacks",
          }),
        );
      }
    },
    [reportPapEvent],
  );

  return { reportToggleStackIsPinned };
}

export function useLeaveStack(
  stack: stacks.Stack | null,
  currentAccount: users.FullAccount | undefined,
) {
  const { reportPapEvent } = useMirageAnalyticsContext();
  const navigate = useNavigate();

  const onLeaveStack = useCallback(async () => {
    if (!currentAccount || !stack) return;

    reportPapEvent(
      PAP_Leave_DashStack({
        ...stackDerivePAPProps(stack),
        featureLine: "stacks",
        actionSurfaceComponent: "stacks_dropdown",
      }),
    );

    // Don't navigate if we leave from the homepage
    if (
      location.pathname !== "/" &&
      location.pathname !== "/home" &&
      location.pathname !== "/stacks"
    ) {
      showSnackbar({ title: i18n.t("leave_stack") });
      navigate("/stacks");
    }

    leaveStack(stack.namespace_id as string, currentAccount.email);
  }, [currentAccount, navigate, reportPapEvent, stack]);

  if (!currentAccount || !stack?.namespace_id || isStackOwner(stack)) {
    return undefined;
  }

  return onLeaveStack;
}

export const useStackMutationForRequestId = (requestId: string) => {
  const [tempStack, setTempStack] = useState<stacks.Stack | null>(null);
  const [tempStackItems, setTempStackItems] = useState<stacks.StackItem[]>([]);
  const [createdNamespaceId, setCreatedNamespaceId] = useState<string | null>(
    null,
  );

  const resetMutationState = useCallback(() => {
    setTempStack(null);
    setTempStackItems([]);
    setCreatedNamespaceId(null);
  }, []);

  useEffect(() => {
    const handleStackMutation = (event: StackMutationEvent) => {
      if (event.requestId !== requestId) {
        return; // Ignore unrelated events
      }

      if (event.eventType === "optimisticEdit") {
        setTempStack(event.stack);
        setTempStackItems(event.stackItemsForStack);
      } else if (event.eventType === "stackCreated") {
        setCreatedNamespaceId(event.namespaceId);
      }
    };

    const subscription = subscribeToStackMutation(handleStackMutation);

    return () => {
      // Cleanup subscription when the component unmounts or requestId changes
      subscription.unsubscribe();
    };
  }, [requestId]);

  return {
    tempStack,
    tempStackItems,
    createdNamespaceId,
    resetMutationState,
  };
};

export const useDefaultItemsToAdd = (
  stackItems: stacks.StackItemShortcut[] | null,
  contentSuggestions: TabSuggestion[],
): TabSuggestion[] => {
  return useMemo(() => {
    const itemUrls = stackItems
      ? new Set(stackItems.map((item) => item.url))
      : new Set();
    const itemTitles = stackItems
      ? new Set(stackItems.map((item) => stackItemComputeNameFromFields(item)))
      : new Set();

    return contentSuggestions
      ? uniqBy(
          contentSuggestions.filter(
            (suggestion) =>
              !(
                itemUrls.has(suggestion.url ?? "") ||
                itemTitles.has(suggestion.title ?? "")
              ),
          ),
          (suggestion) => suggestion.url,
        )
      : [];
  }, [contentSuggestions, stackItems]);
};

interface ProcessedItem {
  hydratedItem: StackItemShortcutWithMetadata;
  updatedItem: stacks.StackItem | null;
  shouldUpdateLastModified: boolean;
}

export const useEnrichedStackItems = (
  stackNamespaceId?: string,
  stackItems?: stacks.StackItem[],
): StackItem[] => {
  const [hydratedItems, setHydratedItems] = useState<StackItem[]>([]);
  const currentAccount = useDropboxAccount();
  const isUpdatingItems = useRef(false);

  const [urlItems, nonUrlItems] = usePartitionStackByUrl(stackItems ?? []);

  const urlsToHydrate = useDeepCompareMemo(
    () =>
      urlItems.map((item) => ({
        url: item.url,
        brandedType: item.branded_type,
        id3p: item.id_3p,
      })),
    [urlItems],
  );

  const processItem = useCallback(
    (
      item: stacks.StackItemShortcut & {
        url: string;
      },
      itemMetadata: UrlMetadata,
    ): ProcessedItem => {
      // Ensure metadata exists for the item
      if (!itemMetadata) {
        return {
          hydratedItem: item,
          updatedItem: null,
          shouldUpdateLastModified: false,
        };
      }

      const itemOwner = isStackItemCreator(currentAccount?.email, item);

      // If the item is owned by the current user, use the metadata title
      // returned from `metadataForUrls`. Otherwise, use the metadata title
      // that is on the item server side.
      // There is a gap here with favicons and thumbnails, as we don't store
      // them on the server side. We should consider storing them in the future.
      // If we had that data server side, we would filter out all unowned stack
      // items from this processing step.
      const metadataTitle = itemOwner
        ? truncateMetadataTitle(itemMetadata?.title)
        : item.metadata_title;

      const hydratedItem: StackItemShortcutWithMetadata = {
        ...item,
        metadata_title: metadataTitle,
        faviconUrl: itemMetadata?.faviconUrl,
        thumbnailUrl: itemMetadata?.thumbnailUrl,
        uuid: itemMetadata?.uuid,
        summarizableTag: itemMetadata?.summarizableTag,
        previewUrl: itemMetadata?.previewUrl ?? undefined,
      };

      let updatedItem: ProcessedItem["updatedItem"] = null;
      let shouldUpdateLastModified: ProcessedItem["shouldUpdateLastModified"] =
        false;
      // If the metadata title is different from the existing title, update the stack item/
      // Only update the item if the user is the owner of the item.
      if (itemOwner && metadataTitle && metadataTitle !== item.metadata_title) {
        updatedItem = {
          ...item,
          metadata_title: metadataTitle,
        };
      } else if (
        // If the contentLastModified exists and is newer than item.last_modified_time_utc_sec, update the item.
        // We just set the metadata title to the same value and pass in update_last_modified_time = True as arg
        // using shouldUpdateLastModified to update the last_modified
        itemOwner &&
        itemMetadata.contentLastModifiedMs &&
        itemMetadata.contentLastModifiedMs >
          (item.last_modified_time_utc_sec ?? 0) * 1000
      ) {
        updatedItem = {
          ...item,
          metadata_title: metadataTitle,
        };
        shouldUpdateLastModified = true;
      }

      return { hydratedItem, updatedItem, shouldUpdateLastModified };
    },
    [currentAccount?.email],
  );

  const hydrateItems = useCallback(
    async (metadataMap: { [key: string]: UrlMetadata }) => {
      if (!stackNamespaceId) {
        return;
      }

      const processedItems = urlItems.map((item) => {
        // see if metadata exists for the item url, if not strip # from the url and try again
        let metadata = metadataMap[item.url];
        if (!metadata) {
          metadata = metadataMap[item.url.split("#")[0]];
        }
        return processItem(item, metadata);
      });

      const itemsToUpdate = processedItems.filter(
        (data): data is ProcessedItem & { updatedItem: stacks.StackItem } =>
          data.updatedItem !== null,
      );

      isUpdatingItems.current = true;
      for (const { updatedItem, shouldUpdateLastModified } of itemsToUpdate) {
        try {
          await updateStackItem(
            stackNamespaceId,
            updatedItem,
            shouldUpdateLastModified,
          );
        } catch (e) {
          // DASHWEB-4155: Let's see why this is happening (temporary)
          Sentry.withScope((scope) => {
            const stackItemUrl =
              asShortcut(updatedItem)?.url || "UNDEFINED_URL";
            // Set the extra data
            scope.setTag("stackItemUrl", stackItemUrl);
            scope.setTag(
              "errorMessage",
              isErrorWithMessage(e) ? e.message : "unknown",
            );

            // Capture the message
            Sentry.captureMessage(
              "[hydrateItems] update_stack_item FAILED",
              "error",
              {},
              // Ensure scope is passed in here
              scope,
            );
          });
        }
        // Sleep to avoid rate limiting on update endpoint
        await sleepMs(100);
      }
      isUpdatingItems.current = false;

      const newHydratedItems = processedItems.map((data) => data.hydratedItem);
      const allItems: StackItem[] = nonUrlItems.concat(newHydratedItems);
      setHydratedItems(allItems);
    },
    [nonUrlItems, stackNamespaceId, urlItems, processItem],
  );

  useDeepCompareEffect(() => {
    const fetchAndHydrate = (): Subscription | undefined => {
      // If we are currently updating items, don't fetch metadata
      if (isUpdatingItems.current) {
        return;
      }
      const sub = metadataForUrls(urlsToHydrate, {
        includePreviewUrl: true,
      }).subscribe(hydrateItems);
      return sub;
    };

    let sub = fetchAndHydrate();

    // // Set up interval for refreshing every 2 minutes
    const intervalId = setInterval(() => {
      if (sub) {
        sub.unsubscribe(); // Unsubscribe from the previous subscription
      }
      sub = fetchAndHydrate(); // Create a new subscription
    }, ONE_MINUTE_IN_MILLIS * 2);

    return () => {
      clearInterval(intervalId);
      if (sub) {
        sub.unsubscribe();
      }
    };
  }, [urlsToHydrate, hydrateItems]);

  useDeepCompareEffect(() => {
    // Reset hydratedItems every time stackItems changes
    setHydratedItems(stackItems ?? []);
  }, [stackItems]);

  return hydratedItems;
};

const isPublishedStack = (
  publishedContent: stacks.PublishedContent,
): publishedContent is stacks.PublishedContentStack =>
  publishedContent?.[".tag"] === "stack" &&
  publishedContent.publish_data?.is_published === true;

export const filteredPublishedContentsAtom = atom((get) =>
  get(publishedContentsAtom).filter(isPublishedStack),
);

// Feature gated hook that will return company stacks along with a user's own stacks w./ merged data
// will return just user's unsorted
export function useUnsortedStacksWithCompanyStacks() {
  const isCompanyStacksEnabled = useIsCompanyStacksEnabled();
  const unsortedStacks = useAtomValue(unsortedStacksAtom); // non-published stacks
  const companyStacks = useAtomValue(filteredPublishedContentsAtom);

  // combines user stacks and company pinned stacks
  const mergedStacks = useMemo(() => {
    return mergeAndAggregateUniqueStacksByNamespaceId(
      unsortedStacks || [],
      companyStacks,
    );
  }, [unsortedStacks, companyStacks]);

  return isCompanyStacksEnabled ? mergedStacks : unsortedStacks;
}

const STACK_IS_WELCOME_STACK = (stack: stacks.Stack) =>
  stack?.stack_data?.creation_type?.[".tag"] === "welcome_stack";

export function useWelcomeStacks() {
  const stacks = useAtomValue(unsortedStacksAtom) || [];
  return stacks.filter(STACK_IS_WELCOME_STACK);
}

export function useHydrateCompanyStacksPreviewData() {
  const unsortedStacks = useAtomValue(unsortedStacksAtom);
  const isCompanyStacksEnabled = useIsCompanyStacksEnabled();

  const callPreviewStacksForNotCached = useCallback(
    (contents: stacks.PublishedContent[]) => {
      if (!isCompanyStacksEnabled) {
        return;
      }
      contents?.forEach((c) => {
        if (c[".tag"] === "stack" && c.namespace_id) {
          const shareId = stackGetShareId(c);
          const stack = unsortedStacks?.find(
            (s) => s.namespace_id === c.namespace_id,
          );
          if (!stack && shareId) {
            previewStack(shareId, { refresh: true, fetchInBackground: true });
          }
        }
      });
    },
    [isCompanyStacksEnabled, unsortedStacks, stackGetShareId, previewStack],
  );

  return { callPreviewStacksForNotCached };
}

export function useUpdateStackWithCustomColor(
  stack: stacks.Stack | null,
  mutationRequestId?: string,
) {
  const { reportPapEvent } = useMirageAnalyticsContext();

  const updateStackCustomColor = useCallback(
    async (customHexColor: string) => {
      try {
        if (!isValidHexColor(customHexColor)) {
          showSnackbar({ title: i18n.t("invalid_hex_color") });
          return;
        }

        const hasExistingCustomColor = isValidHexColor(
          stack?.stack_data?.custom_color,
        );

        await upsertStack(
          {
            type: stack?.namespace_id ? "nsId" : "requestId",
            id: stack?.namespace_id
              ? nonNil(stack.namespace_id, "stack.namespace_id")
              : nonNil(mutationRequestId, "mutationRequestId"),
          },
          [
            {
              field: {
                ".tag": "custom_color_update",
                custom_color_update: customHexColor,
              },
            },
            {
              field: {
                ".tag": "color_index_update",
                color_index_update: StackTheme.Custom,
              },
            },
          ],
        );
        if (!stack) {
          return;
        }
        if (hasExistingCustomColor) {
          reportPapEvent(
            PAP_Update_StackIconColor({
              ...stackDerivePAPProps(stack),
              actionSurfaceComponent: "stacks_dropdown",
              featureLine: "stacks",
              stackIconUpdate: "custom_edit",
            }),
          );
        } else {
          reportPapEvent(
            PAP_Update_StackIconColor({
              ...stackDerivePAPProps(stack),
              actionSurfaceComponent: "stacks_dropdown",
              featureLine: "stacks",
              stackIconUpdate: "custom",
            }),
          );
        }
      } catch (e) {
        showSnackbar({ title: i18n.t("failed_to_update_color") });
      }
    },
    [stack, mutationRequestId, isValidHexColor, upsertStack],
  );
  return updateStackCustomColor;
}

export function usePartitionStackByUrl(stackItems: stacks.StackItem[]) {
  return useDeepCompareMemo(() => {
    return partition(
      stackItems,
      (item) => isStackItemShortcut(item) && isStackItemWithUrl(item),
    );
  }, [stackItems]);
}

// We only care if the item is summarizable which rarely changes
const TWENTY_FOUR_HOURS_IN_MILLIS = 24 * 60 * 60 * 1000;
export function useUrlMetadataForStacks(
  stackItemsMap?: {
    [nsid: string]: stacks.StackItem[];
  },
  isStacksPageTabFeatureEnabled?: boolean,
) {
  const [urlItems] = usePartitionStackByUrl(
    Object.values(stackItemsMap ?? {}).flat(),
  );
  const urlsToHydrate = urlItems.map((item) => item.url);

  const urlMetadataMap = useMetadataForUrls(
    isStacksPageTabFeatureEnabled ? urlsToHydrate : [],
    {
      includePreviewUrl: true,
      timeToLive: TWENTY_FOUR_HOURS_IN_MILLIS,
    },
  );
  return urlMetadataMap;
}

export function useChatSourcesForStacks(
  stackItemsMap: {
    [nsid: string]: stacks.StackItem[];
  },
  stacks: stacks.Stack[],
  isStacksPageTabFeatureEnabled: boolean,
  isDarkMode: boolean,
) {
  const urlMetadataMap = useUrlMetadataForStacks(
    stackItemsMap,
    isStacksPageTabFeatureEnabled,
  );
  const chatSourcesMap: { [nsid: string]: ChatSource[] } = {};
  Object.entries(stackItemsMap).forEach(([nsid, stackItems]) => {
    const summarizableStackItems = isStacksPageTabFeatureEnabled
      ? stackItems?.reduce((acc, stackItem) => {
          if (isStackItemShortcut(stackItem) && isStackItemWithUrl(stackItem)) {
            const urlMetadata = urlMetadataMap?.[stackItem.url];
            if (urlMetadata) {
              const summarizableStackItem: StackItemShortcutWithMetadata = {
                ...stackItem,
                faviconUrl: urlMetadata?.faviconUrl,
                thumbnailUrl: urlMetadata?.thumbnailUrl,
                previewUrl: urlMetadata?.previewUrl ?? undefined,
                uuid: urlMetadata?.uuid,
                summarizableTag: urlMetadata?.summarizableTag,
              };
              if (isSummarySupported(summarizableStackItem)) {
                acc.push(summarizableStackItem);
              }
            }
          }

          return acc;
        }, [] as StackItemShortcutWithMetadata[])
      : undefined;
    const chatSources =
      summarizableStackItems?.map((item) =>
        convertStackItemToChatSource(item, stacks ?? [], isDarkMode ?? false),
      ) ?? [];
    chatSourcesMap[nsid] = chatSources;
  });
  return chatSourcesMap;
}

export function truncateMetadataTitle(str?: string): string {
  if (!str) {
    return "";
  }
  return str.length > MAX_STACK_ITEM_TITLE_LENGTH
    ? str.slice(0, MAX_STACK_ITEM_TITLE_LENGTH)
    : str;
}

export const useArchiveStack = (stack: stacks.Stack | null) => {
  const { reportPapEvent } = useMirageAnalyticsContext();
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const isArchiveStacksEnabled = !!stack?.namespace_id;

  const toggleStackArchiveStatus = useCallback(
    async (forceArchive?: boolean, isUndo?: boolean) => {
      const namespaceId = stack?.namespace_id;
      if (!namespaceId) {
        return;
      }

      const shouldArchive = forceArchive ?? !stackIsArchived(stack);
      const archiveUpdate: stacks.StackDataUpdate = {
        field: {
          is_archived: shouldArchive,
          ".tag": "archive_update",
        },
      };
      const papEvent = shouldArchive
        ? PAP_Archive_DashStacksArchive
        : PAP_Undo_DashStacksArchive;
      reportPapEvent(
        papEvent({
          ...stackDerivePAPProps(stack),
          actionSurfaceComponent: "stacks_dropdown",
        }),
      );

      try {
        const response = await updateStack(namespaceId, [archiveUpdate]);

        showSnackbar({
          title: i18n.t(
            shouldArchive
              ? "archived_stack_snackbar"
              : "unarchived_stack_snackbar",
            { stackName: stack?.stack_data?.name },
          ),
          buttons: isUndo
            ? []
            : [
                {
                  label: i18n.t("undo"),
                  onClick: () => toggleStackArchiveStatus(shouldArchive, true),
                },
              ],
        });

        return response.stack;
      } catch (e) {
        Sentry.captureException(e);
        showSnackbar({
          title: i18n.t("failed_to_action_stack", {
            action: shouldArchive ? "archive" : "unarchive",
          }),
        });
        return null;
      }
    },
    [stack, reportPapEvent],
  );

  return {
    isArchiveStacksEnabled,
    isArchiveModalOpen,
    setIsArchiveModalOpen,
    toggleStackArchiveStatus,
  };
};

const convertFileToBase64 = async (file: File): Promise<string> => {
  const fileContent = await file.arrayBuffer();
  return btoa(
    new Uint8Array(fileContent).reduce(
      (data, byte) => data + String.fromCharCode(byte),
      "",
    ),
  );
};

export function useUpdateStackLogo(stack: stacks.Stack | null) {
  const updateStackLogo = useCallback(
    async (file: File | null) => {
      if (file && stack?.namespace_id) {
        const base64String = await convertFileToBase64(file);

        try {
          await updateStack(stack?.namespace_id, [
            {
              field: {
                ".tag": "logo_update",
                content_base64: base64String,
                content_type: file.type,
                content_size: base64String.length,
              },
            },
          ]);
        } catch (error) {
          // https://dropbox.atlassian.net/browse/BFD-1072
          // improve the error handling with handling large files and transient errors
          showSnackbar({
            title: i18n.t("failed_to_update_logo"),
          });
          logger.error("Error updating stack logo", error);
          Sentry.captureException(error);
        }
      }
    },
    [stack],
  );

  return updateStackLogo;
}

export function useUpdateStackBanner(stack: stacks.Stack | null) {
  const updateStackBanner = useCallback(
    async (file: File | null) => {
      if (file && stack?.namespace_id) {
        const base64String = await convertFileToBase64(file);
        try {
          await updateStack(stack?.namespace_id, [
            {
              field: {
                ".tag": "banner_update",
                content_base64: base64String,
                content_type: file.type,
                content_size: base64String.length,
              },
            },
          ]);
        } catch (error) {
          // https://dropbox.atlassian.net/browse/BFD-1072
          // improve the error handling with handling large files and transient errors
          showSnackbar({
            title: i18n.t("failed_to_update_banner"),
          });
          logger.error("Error updating stack banner", error);
          Sentry.captureException(error);
        }
      }
    },
    [stack],
  );

  return updateStackBanner;
}

export function useUpdateStackColorIndex(stack: stacks.Stack | null) {
  const updateStackColor = useCallback(
    async (colorIndex: number) => {
      if (stack?.namespace_id === undefined) {
        logger.warn(
          "Stack namespace_id is undefined, cannot update color index",
        );
        return;
      }
      try {
        await updateStack(stack?.namespace_id, [
          {
            field: {
              ".tag": "color_index_update",
              color_index_update: colorIndex,
            },
          },
        ]);
      } catch (error) {
        showSnackbar({
          title: i18n.t("failed_to_update_color"),
        });
        logger.error("Error updating stack color", error);
        Sentry.captureException(error);
      }
    },
    [stack],
  );

  return updateStackColor;
}
